import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { translate } from 'services/i18nService';
import Button from 'components/Button/Button';
import StyledTextField from 'components/StyledTextField/StyledTextField';
import APIStates from 'enum/APIStates';
import routeDefinitions from 'enum/routeDefinitions';
import { generateID } from 'utils/helpers';
import { ENUM_TWO_FA } from 'enum/profile';

import styles from './TwoFaModal.css';


function Form(props) {
  const {
    errorMsg,
    handleTwoCode,
    isCodeResent,
    isErrorMsgVisible,
    ids,
    resendTwoFaCode,
    responseForTwoFa,
    submitCodeStatus,
    twoFaCode,
    twoFaType,
    validateTwoFaCode,
  } = props;

  const IDS = {
    reSubmit: 'reSubmit',
    submit: 'submit',
    twoFaCode: 'twoFaCode',
  };
  const makeId = generateID(ids.prefix, ids.element);

  const phoneNumber = `+${responseForTwoFa.countryCode}${responseForTwoFa.phoneNumber}`;

  let twoFaTypeFinal = twoFaType;
  if (twoFaType === ENUM_TWO_FA.TWILIO_VERIFY) {
    twoFaTypeFinal = ENUM_TWO_FA.SMS;
  }

  const translationKeys = {
    [ENUM_TWO_FA.EMAIL]: {
      title: { key: 'account.twoFa.email' },
      preventUnwantedAccess: { key: 'profile.profile.manage.twofa.preventUnwantedAccess.email' },
      enterCode: {
        key: 'profile.profile.manage.twofa.please.enter.code',
        value: responseForTwoFa.email,
      },
      codeSent: {
        key: 'strings.strings.code.is.sent',
        value: { 0: responseForTwoFa.email },
      },
    },
    [ENUM_TWO_FA.SMS]: {
      title: { key: 'profile.manage.twoFa' },
      preventUnwantedAccess: { key: 'profile.profile.manage.twofa.preventUnwantedAccess' },
      enterCode: {
        key: 'profile.profile.manage.twofa.please.enter.code',
        value: phoneNumber,
      },
      codeSent: {
        key: 'strings.strings.code.is.sent',
        value: {
          0: `+${responseForTwoFa.countryCode !== undefined ? responseForTwoFa.countryCode : ''}${
            responseForTwoFa.phoneNumber
          }`,
        },
      },
    },
  };

  return (
    <div className={styles.twoFaWrapper}>
      <h4 className={styles.smsTitle}>
        {translate(translationKeys[twoFaTypeFinal].title.key)}
      </h4>
      <div className={styles.phoneNumberWrapper}>
        {responseForTwoFa && !isErrorMsgVisible && !isCodeResent && (
          <Fragment>
            <span>{translate('strings.strings.verify.its.you')}</span>
            <p>
              {translate(
                translationKeys[twoFaTypeFinal].enterCode.key,
                { 0: translationKeys[twoFaTypeFinal].enterCode.value }
              )}
            </p>
            <p>
              <i>{translate(translationKeys[twoFaTypeFinal].preventUnwantedAccess.key)}</i>
            </p>
          </Fragment>
        )}
        {isErrorMsgVisible && errorMsg === 'INVALID_CODE' && (
          <div className={styles.errorMsg}>
            <span>{translate('strings.strings.invalid.code.try.again')}</span>
          </div>
        )}
        {isErrorMsgVisible && (errorMsg === 'FAILED' || errorMsg === 'INVALID_PHONE_NUMBER') && (
          <div className={styles.errorMsg}>
            <span>{translate('android.toast_server_error')}</span>
          </div>
        )}
        {isCodeResent && (
          <div className={styles.newCodeSent}>
            <span>
              {translate(
                translationKeys[twoFaTypeFinal].codeSent.key,
                translationKeys[twoFaTypeFinal].codeSent.value,
              )}{' '}
            </span>
          </div>
        )}
      </div>
      <StyledTextField
        id={makeId(IDS.twoFaCode)}
        label={translate('login.twofa.enter.code')}
        type="text"
        value={twoFaCode}
        onChange={handleTwoCode}
      />
      <div className={styles.twoFaBtns}>
        <Button
          className={styles.resendCode}
          id={makeId(IDS.reSubmit)}
          label={translate('profile.profile.manage.twofa.resend.code')}
          onClick={resendTwoFaCode}
        />
        <Button
          colour="orange"
          disabled={twoFaCode === '' || submitCodeStatus === APIStates.PENDING}
          id={makeId(IDS.submit)}
          label={translate('contacts.contacts.button.submit')}
          onClick={validateTwoFaCode}
        />
      </div>
      <div className={styles.twoFaFooterLink}>
        {translate('profile.profile.manage.twofa.need.help.visit.faq.updated', {
          0: `<a target="_blank" rel="noopener noreferrer" href=${routeDefinitions.smsVerification}>`,
          1: '</a>',
        })}
      </div>
    </div>
  );
}

Form.propTypes = {
  handleTwoCode: PropTypes.func.isRequired,
  resendTwoFaCode: PropTypes.func.isRequired,
  responseForTwoFa: PropTypes.object.isRequired,
  submitCodeStatus: PropTypes.string.isRequired,
  validateTwoFaCode: PropTypes.func.isRequired,
  errorMsg: PropTypes.string,
  ids: PropTypes.shape({
    element: PropTypes.string.isRequired,
    prefix: PropTypes.string.isRequired,
  }),
  isCodeResent: PropTypes.bool,
  isErrorMsgVisible: PropTypes.bool,
  twoFaCode: PropTypes.string,
  twoFaType: PropTypes.string,
};

Form.defaultProps = {
  errorMsg: undefined,
  ids: {},
  isCodeResent: false,
  isErrorMsgVisible: false,
  twoFaCode: undefined,
  twoFaType: ENUM_TWO_FA.SMS,
};

export default Form;
